<template>
  <IdentityShell top-link="none">
    <div class="directory-entry">
      <p class="kicker">
        {{ $t('auth.identity.directoryEntry.signIn', 'Sign in') }}
      </p>

      <h1 class="section-headline directory-entry__title">
        {{ $t('auth.identity.directoryEntry.signInToYour', 'Sign in to your') }}
        <span class="highlight">{{
          $t(
            'auth.identity.directoryEntry.organizationHighlight',
            'organization'
          )
        }}</span
        >{{ $t('auth.identity.directoryEntry.organizationTrail', '.') }}
      </h1>
      <p class="section-lede directory-entry__lede">
        {{
          $t(
            'auth.identity.directoryEntry.signInCodeSub',
            "Enter your email and we'll send a secure 6-digit sign-in code."
          )
        }}
      </p>

      <AlertInfo v-if="notice" show class="mb-3">
        {{ notice }}
      </AlertInfo>

      <AlertDanger v-if="firstError" show class="mb-3">
        {{ firstError }}
      </AlertDanger>

      <FormBase class="auth-form" @submit.prevent="onSubmit">
        <FormGroupBase
          class="mb-3"
          :label="$t('auth.identity.directoryEntry.emailLabel', 'Email')"
          :state="fieldState('email')"
          :invalid-feedback="fieldError('email')"
        >
          <InputEmail
            v-model="email"
            autofocus
            autocomplete="email"
            :placeholder="
              $t(
                'auth.identity.directoryEntry.emailPlaceholder',
                'you{\'@\'}example.com'
              )
            "
            :state="fieldState('email')"
          />
        </FormGroupBase>

        <button
          type="submit"
          class="btn-communal btn-communal--block"
          :disabled="submitting"
        >
          <span v-if="!submitting">
            {{
              $t(
                'auth.identity.directoryEntry.sendSignInCode',
                'Send sign-in code'
              )
            }}
            <span class="btn-communal__arrow" aria-hidden="true">→</span>
          </span>
          <span v-else>
            {{
              $t('auth.identity.directoryEntry.sendingCode', 'Sending code…')
            }}
          </span>
        </button>
      </FormBase>

      <template v-if="hasSocialProviders">
        <div class="auth-divider directory-entry__divider" aria-hidden="true">
          <span class="auth-divider__label">
            {{ $t('auth.identity.directoryEntry.or', 'or') }}
          </span>
        </div>

        <AuthSocialButtons intent="login" />
      </template>

      <p class="auth-foot-link">
        {{
          $t('auth.identity.directoryEntry.notSignedUp', 'Not signed up yet?')
        }}
        <RouterLink to="/signup">
          {{
            $t(
              'auth.identity.directoryEntry.startFreeTrial',
              'Start free trial'
            )
          }}
        </RouterLink>
      </p>
    </div>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import { AuthSocialButtons } from '@/components/feature/Auth';
import { IdentityShell } from '@/components/feature/Identity';
import { AlertDanger, AlertInfo } from '@/components/ui/Alert';
import { InputEmail } from '@/components/ui/Form';
import { useIdentityBoot } from '@/composables/useIdentityBoot';
import { useIdentityForm } from '@/composables/useIdentityForm';

const email = ref('');
const hasSocialProviders = computed(() =>
  Boolean(window.socialProviders?.google)
);

// One-shot notice surfaced by the server (e.g. the tenant signup handoff
// redirected here because the link expired). Cleared when the page mounts
// via useIdentityBoot.
const boot = useIdentityBoot();
const notice = computed(() => {
  const value = boot.data?.notice;
  return typeof value === 'string' && value !== '' ? value : null;
});

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/login' });

async function onSubmit() {
  await submit({ email: email.value });
}
</script>

<style lang="scss" scoped>
// Rendered inside IdentityShell's .communal-surface scope — raw <h1>, raw
// submit <button>, .section-headline, .section-lede, and .btn-communal are
// deliberate marketing-surface classes, not in-product wrappers. See
// IdentityShell.vue for the full contract.
//
// /login is sign-in only. Starting a trial lives on /signup; the only pointer
// to it here is the small foot link so the page's intent stays unambiguous.

.directory-entry__title {
  margin-bottom: 0.75rem;
}

.directory-entry__lede {
  margin-bottom: 1.75rem;
}

.directory-entry__divider {
  margin: 1.25rem 0 1rem;
}
</style>
